// Toast (§11.5): "Pasted to N photos" with Undo (restores every pasted photo at once).
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import d from './Dialog.module.css';
import { useClipboard } from '../../state/clipboard';
import { useEditor } from '../../state/editor';
import { useT, type MessageKey } from '../../i18n';

export function Toast() {
  const t = useT();
  const toast = useClipboard((st) => st.toast);
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(
      () => useClipboard.getState().setToast(null),
      toast.ms ?? (toast.undo || toast.action ? 8000 : 2500),
    );
    return () => clearTimeout(id);
  }, [toast]);
  if (!toast) return null;
  const text = t(toast.key, toast.vars);
  return (
    <div
      role="status"
      data-testid="toast"
      style={{
        // Top of the photo area, just under the 52 px top bar: never over the bottom bar or a panel button.
        position: 'fixed',
        left: '50%',
        top: 64,
        transform: 'translateX(-50%)',
        zIndex: 90,
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        padding: '8px 14px',
        background: 'var(--toast-bg)',
        color: 'var(--toast-text)',
        borderRadius: 'var(--radius)',
      }}
    >
      <span>{text}</span>
      {toast.undo && (
        <button
          data-testid="toast-undo"
          onClick={() => {
            useEditor.getState().restoreMany(toast.undo!.before);
            useClipboard.getState().setToast(null);
          }}
        >
          {toast.key === 'pastedN' ? t('undoPasteN', { n: toast.undo.n }) : t('undo')}
        </button>
      )}
      {toast.action && (
        <button
          data-testid="toast-action"
          onClick={() => {
            toast.action!.run();
            useClipboard.getState().setToast(null);
          }}
        >
          {t(toast.action.key)}
        </button>
      )}
    </div>
  );
}

/**
 * M18: a notice shown once per browser as a small dismissible toast (instead of a full-width bar), e.g. "Your
 * browser can't save next to your photos". Remembered as dismissed in localStorage.
 */
export function NoticeOnce({ show, id, message }: { show: boolean; id: string; message: MessageKey }) {
  const t = useT();
  const key = `epb.notice.${id}`;
  const [gone, setGone] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  if (!show || gone) return null;
  const dismiss = () => {
    setGone(true);
    try {
      localStorage.setItem(key, '1');
    } catch {
      /* not remembered */
    }
  };
  return (
    <div role="status" data-testid={`notice-${id}`} className={d.notice}>
      <span>{t(message)}</span>
      <button
        className={d.noticeX}
        aria-label={t('noticeDismiss')}
        title={t('noticeDismiss')}
        onClick={dismiss}
      >
        <X className="icon" aria-hidden="true" />
      </button>
    </div>
  );
}
